import { QuoteHistory } from '@/components/workorders/QuoteHistory';
import { WorkOrderEmailDeliveries } from '@/components/workorders/WorkOrderEmailDeliveries';
import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { PartsOrdersCard } from '@/components/workorders/PartsOrdersCard';
import { WorkOrderNotesPreview } from '@/components/workorders/WorkOrderNotesPreview';
import { DropoffBanner } from '@/components/workorders/DropoffBanner';

import { WorkOrderHeader, CustomerInfoTab, VehicleInfoTab, WorkOrderTabbedCard, WorkOrderItemsTable, WorkOrderNotesDialog, WorkOrderServicesSection, WorkOrderPayments, WorkOrderNotesHistory, VehicleServiceHistoryCard, ConfirmationMethodDialog, UndoConfirmDialog, WorkOrderStatusActions, WorkOrderStatusInfo, WorkOrderScheduleBar, MechanicPartsPanel } from '@/components/workorders';
import { WorkOrderTimeEntriesSection } from '@/components/workorders/WorkOrderTimeEntriesSection';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  availableWorkOrderDetailCards,
  mergeCardOrder,
  useWorkOrderDetailCardLayout,
  type DetailCardId,
} from './useWorkOrderDetailCardLayout';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useWorkOrderDetailDialogs } from './useWorkOrderDetailDialogs';
import { useWorkOrderServiceActions } from './useWorkOrderServiceActions';
import { PauseWorkDialog } from '@/components/workorders/PauseWorkDialog';
import { VehicleLocationCard } from '@/components/workorders/VehicleLocationCard';
import { VehicleLocationDialog } from '@/components/workorders/VehicleLocationDialog';
import { WorkOrderPartiesPanel } from '@/components/workorders/WorkOrderPartiesPanel';
import { NoWhatsAppRecipients } from '@/components/workorders/sms/NoWhatsAppRecipients';
import { AttachmentsCard } from '@/components/attachments/AttachmentsCard';
import { ServiceBookChecklistsCard } from '@/components/workorders/ServiceBookChecklistsCard';
import { WorkPathSection } from '@/components/workorders/WorkPathSection';
import { DeliveryReportButton } from '@/components/workorders/DeliveryReportButton';
import { WorkOrderPrintDialog } from '@/components/workorders/WorkOrderPrintDialog';
import { MileageRequiredDialog } from '@/components/workorders/MileageRequiredDialog';
import { WorkOrderReasonDialog } from '@/components/workorders/WorkOrderReasonDialog';
import { ServiceBookOverrideDialog } from './ServiceBookOverrideDialog';
import {
  isServiceBookApplicable,
  isServiceBookUnfilledError,
  SERVICE_BOOK_OVERRIDE_PERMISSION,
  type ServiceBookAudit,
} from './serviceBookCompletion';
import { PreInvoiceChecklistDialog } from '@/components/workorders/PreInvoiceChecklistDialog';
import {
  useWorkCompletionGate,
  WorkCompletionGateContext,
} from '@/components/workorders/completion/useWorkCompletionGate';
import { useUpdateServiceBook } from '@/hooks/useChecklists';
import { AcceptQuoteScheduleDialog } from '@/components/workorders/AcceptQuoteScheduleDialog';
import { SendQuoteDialog } from '@/components/workorders/SendQuoteDialog';
import { acceptQuoteErrorMessage, rejectQuoteErrorMessage } from '@/components/workorders/acceptQuoteError';
import { useWorkOrderInvoice } from '@/hooks/useInvoices';
import { useAuthStore } from '@/stores/authStore';
import { INVOICE_READ_PERMISSIONS } from '@/lib/invoicePermissions';
import { apiErrorCode, describeApiError } from '@/lib/describeApiError';
import { formatCentsForDisplay } from '@/utils/money';
import { WorkOrderOffersCard } from '@/components/workorders/offers/WorkOrderOffersCard';
import {
  isPendingOfferStatus,
  useWorkOrderOffers,
} from '@/hooks/useWorkOrderOffers';
import { offerMoneyBuckets } from '@/components/workorders/offers/offerDisplay';
import { useWorkOrder, useUpdateBookingStatus, useWorkOrderHistory } from '@/hooks';
import { SortableCollapsibleCard } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useStartService,
  usePauseService,
  useResumeService,
  useReopenService,
  useCompleteService,
  useVehicleArrived,
  useReadyToInvoice,
  useResumeWorkOrder,
  useSetWaitingForParts,
  usePartsReceived,
  useRejectQuote,
  useDispute,
  useResolveDispute,
  useInvoicingHold,
  useResolveInvoicingHold,
  useCancelWorkOrder,
  useDeleteWorkOrder,
  useUndoWorkOrder,
  useWorkOrderPartsStatus,
  useSendQuote,
  type SendQuoteData,
} from '@/hooks/useWorkOrders';
import {
  useUpdateBooking,
  useAddBookingService,
  useRemoveBookingService,
  useReorderBookingServices,
  useConfirmBooking,
  useAcceptQuote,
  type AcceptQuoteData,
} from '@/hooks/useBookings';
import { useMechanics } from '@/hooks/useStaffUsers';
import { useAddBookingNote } from '@/hooks/useBookingNotes';
import { useNotify } from '@/stores/notificationStore';
import { useEntitySync } from '@/hooks/useEntitySync';
import { useSveaPaymentReceivedToast } from './useSveaPaymentReceivedToast';
import { getCustomerDisplayName } from '@/utils/customer';
import { courtesyWorkOrderCardSection } from './WorkOrderDetailCards';
import { WorkOrderVehicleCard } from '@/components/workorders/WorkOrderVehicleCard';
import {
  WorkOrderApprovalCard,
  isWorkOrderSignatureRequiredError,
  openWorkOrderApprovalCard,
} from '@/components/workorders/signature';

interface WorkOrderDetailContentProps {
  workOrderId: string;
  onClose: () => void;
  initialEditOpen?: boolean;
  onEditOpened?: () => void;
  onDirtyStateChange?: (isDirty: boolean) => void;
  cardVariant?: 'elevation' | 'outlined';
}

export function WorkOrderDetailContent({ workOrderId, onClose, initialEditOpen = false, onEditOpened, onDirtyStateChange, cardVariant = 'elevation' }: WorkOrderDetailContentProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { t: tChecklists } = useTranslation('checklists');
  const notify = useNotify();
  type WorkOrderToastStatus =
    | 'confirmed'
    | 'vehicle_arrived'
    | 'in_progress'
    | 'paused'
    | 'work_completed'
    | 'ready_to_invoice'
    | 'invoiced'
    | 'completed'
    | 'cancelled'
    | 'quote'
    | 'quote_rejected';
  type ServiceToastAction = 'started' | 'paused' | 'resumed' | 'completed' | 'reopened';

  // Real-time entity sync (#2953). Disabled in April for a test and never
  // restored, which left this page deaf to entity:changed: a customer's
  // quote answer, or any edit from another client, showed only after a
  // manual reload. The header renders the viewers; the hook also invalidates
  // the work order queries for this id on every change.
  const { viewers, lastChange } = useEntitySync('workorder', workOrderId);
  useSveaPaymentReceivedToast(workOrderId);
  const lastChangeRef = useRef(lastChange);
  useEffect(() => {
    if (lastChange && lastChange !== lastChangeRef.current) {
      const scopeKey = `realtime.scopeDesc.${lastChange.scope}`;
      notify.info(tc('realtime.updatedByOther'), tc(scopeKey));
    }
    lastChangeRef.current = lastChange;
  }, [lastChange, notify, tc]);

  // Fetch work order from API
  const { data: workOrder, isLoading, error } = useWorkOrder(workOrderId);
  const { data: offersData } = useWorkOrderOffers(workOrderId);
  const { data: partsStatus } = useWorkOrderPartsStatus(workOrderId);
  const updateStatus = useUpdateBookingStatus();
  const updateBooking = useUpdateBooking();
  const addService = useAddBookingService();
  const removeService = useRemoveBookingService();
  const reorderServices = useReorderBookingServices();
  const confirmBooking = useConfirmBooking();
  const acceptQuote = useAcceptQuote();
  const { data: mechanics = [] } = useMechanics();

  // Service lifecycle hooks
  const startService = useStartService();
  const pauseService = usePauseService();
  const resumeService = useResumeService();
  const reopenService = useReopenService();
  // The service a pending "return to in progress" applies to (#3403).
  const [reopenServiceId, setReopenServiceId] = useState<string | null>(null);
  const completeService = useCompleteService();

  // Manual WO status hooks
  const vehicleArrived = useVehicleArrived();
  const readyToInvoice = useReadyToInvoice();
  const resumeWorkOrder = useResumeWorkOrder();
  const updateServiceBook = useUpdateServiceBook();

  // New status lifecycle hooks
  const rejectQuote = useRejectQuote();
  const sendQuote = useSendQuote();
  const [sendQuoteDialogOpen, setSendQuoteDialogOpen] = useState(false);
  const dispute = useDispute();
  const resolveDispute = useResolveDispute();
  const invoicingHold = useInvoicingHold();
  const resolveInvoicingHold = useResolveInvoicingHold();
  const cancelWorkOrder = useCancelWorkOrder();
  const deleteWorkOrder = useDeleteWorkOrder();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // The vehicle card is a modal over the work order (#3400), so nothing here is lost.
  const [vehicleCardOpen, setVehicleCardOpen] = useState(false);
  const addBookingNote = useAddBookingNote();

  // Flag-based hooks (waiting for parts)
  const setWaitingForParts = useSetWaitingForParts();
  const partsReceived = usePartsReceived();

  // Undo hook
  const undoWorkOrder = useUndoWorkOrder();

  // Service states
  const [expandedServiceIds, setExpandedServiceIds] = useState<Set<string>>(new Set());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  // Service whose pause opens the continuation-plan dialog (#548) — set when the
  // paused service is the last active one, so the whole work order will pause.
  const [pauseServiceId, setPauseServiceId] = useState<string | null>(null);
  const {
    cardOrder,
    expandedCards,
    handlesVisible,
    accordion,
    toggleCard,
    toggleHandlesVisible,
    toggleAccordion,
    reorderCards,
  } = useWorkOrderDetailCardLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const openSection = (location.state as { openSection?: string } | null)?.openSection;
  // One-shot expand from post-create navigation (#2900). Kept local so a
  // collapsed saved layout is not overwritten in localStorage.
  const [servicesNavExpand, setServicesNavExpand] = useState(openSection === 'services');

  // The post-create override keeps Services open on its own, so it has to be
  // dropped when the user closes Services or (accordion, #3364) opens another card.
  const toggleDetailCard = (cardId: DetailCardId) => {
    if (servicesNavExpand) {
      if (cardId === 'services') {
        setServicesNavExpand(false);
        if (expandedCards.services) toggleCard('services');
        return;
      }
      if (accordion && !expandedCards[cardId]) setServicesNavExpand(false);
    }
    toggleCard(cardId);
  };

  useEffect(() => {
    if (openSection !== 'services') return;
    // Delay the replace so StrictMode's effect cleanup can cancel the first
    // invoke; otherwise remount would see a cleared state and collapse.
    const timeout = window.setTimeout(() => {
      const { openSection: _consumed, ...remainingState } = (location.state ?? {}) as Record<string, unknown>;
      navigate(location.pathname + (location.search ?? ''), { replace: true, state: Object.keys(remainingState).length ? remainingState : null });
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [openSection, navigate, location.pathname, location.search, location.state]);

  const { serviceBookEnabled, partsOrderingEnabled } = useTenantSettings();

  // Track dirty state for each service detail form
  const [serviceDirtyStates, setServiceDirtyStates] = useState<Record<string, boolean>>({});
  const [scheduleDirty, setScheduleDirty] = useState(false);

  // Aggregated dirty state
  const hasUnsavedChanges = scheduleDirty || Object.values(serviceDirtyStates).some(Boolean);

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyStateChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyStateChange]);

  // Drag-and-drop sensors for service reordering
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const sectionDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Dialog state (notes, edit, reserve, confirm method, location, pause, undo, invoice, parts consumption, reason, mileage)
  const dialogs = useWorkOrderDetailDialogs();
  const {
    notesDialogOpen, setNotesDialogOpen,
    scheduleEditOpen, setScheduleEditOpen,
    confirmMethodOpen, setConfirmMethodOpen,
    locationDialogOpen, setLocationDialogOpen,
    openVehicleLocationDialogIfNeeded,
    acceptQuoteDialogOpen, setAcceptQuoteDialogOpen,
    pauseDialogOpen, setPauseDialogOpen,
    undoDialogOpen, setUndoDialogOpen,
    reasonDialogOpen, setReasonDialogOpen,
    reasonDialogType, setReasonDialogType,
    reasonText, setReasonText,
    mileageDialogOpen, setMileageDialogOpen,
    pendingServiceCompletion, setPendingServiceCompletion,
    mileageDialogLoading, setMileageDialogLoading,
    serviceBookOverrideOpen, setServiceBookOverrideOpen,
    serviceBookOverrideReason, setServiceBookOverrideReason,
    reset: resetDialogs,
  } = dialogs;

  // Print choice of the work-order PDF (#4107).
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  // Ready-to-invoice checklist (#4102): shown before POST /:id/ready-to-invoice.
  const [readyChecklistOpen, setReadyChecklistOpen] = useState(false);

  // "Työ valmis?" (#4101): one dialog for the last labour step or service,
  // shared with the labour rows below through context.
  const completionGate = useWorkCompletionGate(workOrder);

  // Schedule bar — support opening straight in edit mode via prop (`?edit=true`)
  const initialEditApplied = useRef(false);

  // Reset per-work-order UI state when the detail view switches to a different
  // work order (#420). The component instance is intentionally reused across
  // /workorders/:id param changes rather than remounted (see #899), so state
  // tied to the previous work order — expanded services, an in-flight drag,
  // per-service dirty flags and open dialogs — must be cleared explicitly or it
  // leaks into the next one (e.g. a stale dirty flag firing a spurious
  // unsaved-changes guard, or a mileage dialog completing the wrong service).
  useEffect(() => {
    // Bail out when already empty so the reset does not churn out new object
    // references on every workOrderId change — that extra state update fed the
    // render loop this fix targets (#420).
    setExpandedServiceIds((prev) => (prev.size === 0 ? prev : new Set()));
    setActiveDragId((prev) => (prev === null ? prev : null));
    setServiceDirtyStates((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    setScheduleDirty((prev) => (prev ? false : prev));
    initialEditApplied.current = false;
    setReadyChecklistOpen(false);
    resetDialogs();
  }, [workOrderId, resetDialogs]);

  useEffect(() => {
    if (initialEditOpen && !initialEditApplied.current && workOrder) {
      setScheduleEditOpen(true);
      initialEditApplied.current = true;
      onEditOpened?.();
    }
  }, [initialEditOpen, workOrder, onEditOpened, setScheduleEditOpen]);

  const { data: history = [] } = useWorkOrderHistory(workOrderId);

  // Registry records are edited on the person/company pages (#833 B4): the
  // customer-info pencil navigates to the orderer party's page.
  const ordererPartyPath = useMemo(() => {
    const orderer = workOrder?.parties?.find((p) => p.role === 'orderer');
    if (!orderer) return null;
    return orderer.partyType === 'company'
      ? (orderer.companyId ? `/parties/companies/${orderer.companyId}` : null)
      : (orderer.personId ? `/parties/persons/${orderer.personId}` : null);
  }, [workOrder?.parties]);

  // Invoice query (depends on workOrder.status and on invoice read permission —
  // the route is guarded since #1315, so firing this without the permission
  // would only produce a 403)
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canOverrideServiceBook = hasPermission(SERVICE_BOOK_OVERRIDE_PERMISSION);
  const invoiceEnabled = hasAnyPermission(INVOICE_READ_PERMISSIONS)
    && !!workOrder
    && ['ready_to_invoice', 'invoiced', 'completed'].includes(workOrder.status);
  const { data: workOrderInvoice } = useWorkOrderInvoice(workOrderId, invoiceEnabled);

  const {
    handleAddService,
    handleRemoveService,
    handleServiceMechanicChange,
    handleDragStart,
    handleReorderServices,
  } = useWorkOrderServiceActions({
    workOrder,
    updateBooking,
    addService,
    removeService,
    reorderServices,
    setActiveDragId,
    setExpandedServiceIds,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !workOrder) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onClose}
          sx={{ mb: 2 }}
        >
          {tc('back')}
        </Button>
        <Typography variant="h5">{t('detail.notFound')}</Typography>
      </Box>
    );
  }

  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`;
  };
  const embeddedCardContentSx = {
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 3 },
    '& .MuiCard-root': {
      boxShadow: 'none',
      border: 0,
      borderRadius: 0,
      bgcolor: 'transparent',
      mt: '0 !important',
      mb: '0 !important',
    },
    '& .MuiCardContent-root': {
      px: '0 !important',
      pt: '0 !important',
      pb: '0 !important',
      '&:last-child': {
        pb: '0 !important',
      },
    },
    '& .MuiCard-root + .MuiCard-root': {
      mt: 3,
    },
  } as const;
  // The "Työt" (services) section renders a genuine list of per-service
  // cards (SortableServiceCard/ExpandableCard) that need their own border
  // and striping to stay visually distinct from each other — unlike every
  // other section, which nests a single "XCard" component that should be
  // flattened. So it gets padding-only spacing instead of the card reset.
  const servicesCardContentSx = {
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 3 },
  } as const;

  const workOrderNumber = workOrder.workOrderNumber ?? t('noWorkOrderNumber');
  const offers = offersData?.offers ?? [];
  const pendingOfferCount = offers.filter((offer) => isPendingOfferStatus(offer.status)).length;
  const awaitingOfferCents = offerMoneyBuckets(offers).awaiting;
  const hasPendingOffer = pendingOfferCount > 0;
  const showOffersCard =
    workOrder.status !== 'quote' &&
    workOrder.status !== 'quote_rejected' &&
    workOrder.status !== 'cancelled';
  const serviceBookAudit = workOrder as typeof workOrder & ServiceBookAudit;
  const hasPartsOrders = !!partsStatus && (partsStatus.onOrderParts.length > 0 || partsStatus.neededParts.length > 0);
  const partsOrderSummary = hasPartsOrders
    ? t('detail.partsOrderSummary', { ordered: partsStatus.onOrderParts.length, notOrdered: partsStatus.neededParts.length })
    : t('detail.noActivePartsOrders');
  const availableCardIds = availableWorkOrderDetailCards({
    hasPartsOrders,
    serviceBookEnabled,
    partsOrderingEnabled,
  });
  const orderedCardIds = mergeCardOrder(cardOrder, availableCardIds);
  const showStatusToast = (status: WorkOrderToastStatus) => {
    notify.success(
      t(`toasts.status.${status}.title`),
      t(`toasts.status.${status}.desc`, { number: workOrderNumber }),
    );
  };
  const showServiceToast = (action: ServiceToastAction) => {
    notify.success(
      t(`toasts.service.${action}.title`),
      t(`toasts.service.${action}.desc`, { number: workOrderNumber }),
    );
  };

  /**
   * PF-083: start / ready-to-invoice answer 409 WORK_ORDER_SIGNATURE_REQUIRED
   * when customer approval is missing or lapsed. Say so and open the card.
   */
  const handleSignatureRequired = (error: unknown): boolean => {
    if (!isWorkOrderSignatureRequiredError(error)) return false;
    if (!expandedCards.approval) toggleDetailCard('approval');
    notify.warning(tc('error'), t('errorCode.WORK_ORDER_SIGNATURE_REQUIRED'));
    window.setTimeout(openWorkOrderApprovalCard, 0);
    return true;
  };

  const handleStartWork = () => {
    updateStatus.mutate(
      { id: workOrder.id, data: { status: 'in_progress' } },
      {
        onSuccess: () => showStatusToast('in_progress'),
        onError: (error: unknown) => {
          if (handleSignatureRequired(error)) return;
          notify.error(tc('error'), t('detail.statusChangeFailed'));
        },
      }
    );
  };

  const handleRequestApproval = () => {
    notify.info(t('detail.approvalRequest'), t('detail.approvalRequestSent'));
  };

  const handleServiceAction = async (action: string, bookingServiceId: string) => {
    try {
      switch (action) {
        case 'start':
          await startService.mutateAsync({ workOrderId: workOrder.id, serviceId: bookingServiceId });
          showServiceToast('started');
          break;
        case 'pause': {
          const activeCount = workOrder.services.filter((s) => s.status === 'in_progress').length;
          if (activeCount <= 1) {
            // Pausing the last active service tips the whole work order into
            // paused — capture a continuation plan (#548) via the dialog rather
            // than an immediate quick-pause.
            setPauseServiceId(bookingServiceId);
            setPauseDialogOpen(true);
            return;
          }
          await pauseService.mutateAsync({ workOrderId: workOrder.id, serviceId: bookingServiceId });
          showServiceToast('paused');
          break;
        }
        case 'resume':
          await resumeService.mutateAsync({ workOrderId: workOrder.id, serviceId: bookingServiceId });
          showServiceToast('resumed');
          break;
        case 'reopen':
          // Confirm first, with an optional reason for the history (#3403).
          setReopenServiceId(bookingServiceId);
          openReasonDialog('reopen');
          return;
        case 'complete':
          // The last open service opens "Työ valmis?" (#4101), which records
          // the reading, spot and service book before completing it.
          await completionGate.requestCompletion(
            { kind: 'service', serviceId: bookingServiceId },
            async () => {
              await completeService.mutateAsync({ workOrderId: workOrder.id, serviceId: bookingServiceId });
              showServiceToast('completed');
            },
          );
          break;
      }
    } catch (error: unknown) {
      // Check if error is MILEAGE_REQUIRED
      if (
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'error' in error.data &&
        error.data.error === 'MILEAGE_REQUIRED'
      ) {
        setPendingServiceCompletion(bookingServiceId);
        setMileageDialogOpen(true);
        return;
      }
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('serviceActions.serviceActionFailed') }));
    }
  };

  const handleMileageConfirm = async (mileage: number) => {
    setMileageDialogLoading(true);
    try {
      if (pendingServiceCompletion) {
        // Complete a service with mileage
        await completeService.mutateAsync({
          workOrderId: workOrder.id,
          serviceId: pendingServiceCompletion,
          mileage,
        });
        showServiceToast('completed');
        setPendingServiceCompletion(null);
      }
      setMileageDialogOpen(false);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('serviceActions.serviceActionFailed') }));
    } finally {
      setMileageDialogLoading(false);
    }
  };

  const handleMileageCancel = () => {
    setMileageDialogOpen(false);
    setPendingServiceCompletion(null);
  };

  const handleVehicleArrived = async () => {
    try {
      // Arrival itself is the confirmation: the customer brought the vehicle.
      // Record in_person without asking how the booking was confirmed (#2334).
      await vehicleArrived.mutateAsync({
        id: workOrder.id,
        customerConfirmed: true,
        confirmationMethod: 'in_person',
      });
      showStatusToast('vehicle_arrived');
      openVehicleLocationDialogIfNeeded(workOrder.vehicleLocation);
    } catch {
      notify.error(tc('error'), t('detail.statusChangeFailed'));
    }
  };

  const handleConfirmationMethodSelect = async (method: 'phone' | 'email' | 'in_person') => {
    try {
      await confirmBooking.mutateAsync({
        id: workOrder.id,
        confirmationMethod: method,
      });
      showStatusToast('confirmed');
    } catch {
      notify.error(tc('error'), t('detail.statusChangeFailed'));
    } finally {
      setConfirmMethodOpen(false);
    }
  };

  const handleSetWaitingForParts = async () => {
    try {
      await setWaitingForParts.mutateAsync(workOrder.id);
      notify.success(t('workOrderFlags.waitingForPartsSet'), t('workOrderFlags.waitingForPartsSetDesc'));
    } catch {
      notify.error(tc('error'), t('detail.statusChangeFailed'));
    }
  };

  const handlePartsReceived = async () => {
    try {
      await partsReceived.mutateAsync(workOrder.id);
      notify.success(t('workOrderFlags.partsReceived'), t('workOrderFlags.partsReceivedDesc'));
    } catch {
      notify.error(tc('error'), t('detail.statusChangeFailed'));
    }
  };

  // The Payments card shows a service's labour as one locked line (#2638);
  // its steps are edited here in the Works card, so open that service there.
  const handleOpenServiceLabor = (bookingServiceId: string) => {
    const service = workOrder.services.find((entry) => entry.bookingServiceId === bookingServiceId);
    if (!service) return;
    if (!expandedCards.services) toggleDetailCard('services');
    setExpandedServiceIds((prev) => {
      if (prev.has(service.id)) return accordion && prev.size > 1 ? new Set([service.id]) : prev;
      const next = accordion ? new Set<string>() : new Set(prev);
      next.add(service.id);
      return next;
    });
    // Scroll once the card has rendered open. The id is a UUID, safe in a selector.
    window.requestAnimationFrame(() => {
      document
        .querySelector(`[data-booking-service-id="${bookingServiceId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  // The checklist comes first (#4102): parts and labour recorded, the final
  // inspection (may stay open, with a warning) and an unfilled service book.
  const handleReadyToInvoice = () => {
    setReadyChecklistOpen(true);
  };

  const submitReadyToInvoice = async () => {
    try {
      await readyToInvoice.mutateAsync(workOrder.id);
      setReadyChecklistOpen(false);
      showStatusToast('ready_to_invoice');
      setServiceBookOverrideOpen(false);
    } catch (error: unknown) {
      setReadyChecklistOpen(false);
      if (isServiceBookUnfilledError(error)) {
        if (canOverrideServiceBook) {
          setServiceBookOverrideOpen(true);
        }
        notify.error(tc('error'), t('serviceBook.unfilledInvoice'));
        return;
      }
      if (handleSignatureRequired(error)) return;
      if (apiErrorCode(error) === 'WORK_ORDER_OFFER_PENDING') {
        notify.error(tc('error'), t('errorCode.WORK_ORDER_OFFER_PENDING'));
        return;
      }
      notify.error(tc('error'), t('detail.statusChangeFailed'));
    }
  };

  const handleServiceBookOverrideSubmit = async () => {
    const reason = serviceBookOverrideReason.trim();
    if (!reason) return;
    try {
      await updateServiceBook.mutateAsync({
        workOrderId: workOrder.id,
        overrideReason: reason,
      });
      await readyToInvoice.mutateAsync(workOrder.id);
      showStatusToast('ready_to_invoice');
      setServiceBookOverrideOpen(false);
      setServiceBookOverrideReason('');
    } catch (error: unknown) {
      if (handleSignatureRequired(error)) return;
      if (apiErrorCode(error) === 'WORK_ORDER_OFFER_PENDING') {
        notify.error(tc('error'), t('errorCode.WORK_ORDER_OFFER_PENDING'));
        return;
      }
      notify.error(tc('error'), t('detail.statusChangeFailed'));
    }
  };

  const handlePauseWork = () => {
    setPauseDialogOpen(true);
  };

  // Opens the print choice (labour summarised or itemised, #4107); the dialog
  // opens the print window inside its own click.
  const handlePrintWorkOrder = (): void => {
    setPrintDialogOpen(true);
  };

  const handleResumeWork = async () => {
    try {
      await resumeWorkOrder.mutateAsync(workOrder.id);
      showStatusToast('in_progress');
    } catch {
      notify.error(tc('error'), t('detail.statusChangeFailed'));
    }
  };

  const handleAcceptQuote = () => {
    setAcceptQuoteDialogOpen(true);
  };

  const handleConfirmAcceptQuote = (data: AcceptQuoteData, expectedUpdatedAt: string) => {
    acceptQuote.mutate(
      { id: workOrder.id, data, expectedUpdatedAt },
      {
        onSuccess: () => {
          setAcceptQuoteDialogOpen(false);
          showStatusToast('quote');
        },
        // A refused assignment (403) and a lift clash (409) are answers the
        // user can act on, not generic failures (#2647).
        onError: (error) => notify.error(tc('error'), acceptQuoteErrorMessage(error, t)),
      }
    );
  };

  const handleRejectQuote = async () => {
    try {
      await rejectQuote.mutateAsync({ id: workOrder.id, expectedUpdatedAt: workOrder.updatedAt });
      showStatusToast('quote_rejected');
    } catch (error) {
      notify.error(tc('error'), rejectQuoteErrorMessage(error, t));
    }
  };

  // Send the quotation to the customer (#2651). The API names the failure
  // (no address on file, delivery refused, demo lock), so pass it through.
  const handleConfirmSendQuote = (data: SendQuoteData) => {
    sendQuote.mutate(
      { id: workOrder.id, data },
      {
        onSuccess: () => {
          setSendQuoteDialogOpen(false);
          notify.success(t('sendQuote.sent'), t('sendQuote.sentDesc'));
        },
        onError: (error) =>
          notify.error(
            t('sendQuote.failed'),
            describeApiError(error, tc, { codePrefix: 'workorders:sendQuote.errorCode', fallback: t('sendQuote.failedDesc') }),
          ),
      },
    );
  };

  const handleResolveDispute = async () => {
    try {
      await resolveDispute.mutateAsync(workOrder.id);
      notify.success(t('workOrderFlags.disputeResolved'), '');
    } catch {
      notify.error(tc('error'), t('detail.statusChangeFailed'));
    }
  };

  const handleResolveInvoicingHold = async () => {
    try {
      await resolveInvoicingHold.mutateAsync(workOrder.id);
      notify.success(t('workOrderFlags.invoicingHoldResolved'), '');
    } catch {
      notify.error(tc('error'), t('detail.statusChangeFailed'));
    }
  };

  const openReasonDialog = (type: 'dispute' | 'invoicing_hold' | 'cancel' | 'reopen') => {
    setReasonDialogType(type);
    setReasonText('');
    setReasonDialogOpen(true);
  };

  const handleReasonDialogSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      switch (reasonDialogType) {
        case 'dispute':
          await dispute.mutateAsync({ id: workOrder.id, reason: reasonText });
          notify.success(t('workOrderFlags.disputeSet'), '');
          break;
        case 'invoicing_hold':
          await invoicingHold.mutateAsync({ id: workOrder.id, reason: reasonText });
          notify.success(t('workOrderFlags.invoicingHoldSet'), '');
          break;
        case 'cancel':
          await cancelWorkOrder.mutateAsync({ id: workOrder.id, reason: reasonText || undefined });
          showStatusToast('cancelled');
          break;
        case 'reopen': {
          if (!reopenServiceId) return;
          const reason = reasonText.trim();
          await reopenService.mutateAsync({
            workOrderId: workOrder.id,
            serviceId: reopenServiceId,
            ...(reason ? { reason } : {}),
          });
          setReopenServiceId(null);
          showServiceToast('reopened');
          break;
        }
      }
      setReasonDialogOpen(false);
    } catch (error) {
      // Say why (#3352): a locked work order or a step already reopened elsewhere.
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('detail.statusChangeFailed') }));
    }
  };

  // Permanent delete for duplicates and test work orders (#3343).
  const handleDeleteWorkOrder = async () => {
    try {
      await deleteWorkOrder.mutateAsync(workOrder.id);
      setDeleteDialogOpen(false);
      notify.success(t('workOrderActions.deleted'), t('workOrderActions.deletedDesc', { number: workOrderNumber }));
      onClose();
    } catch (error) {
      setDeleteDialogOpen(false);
      notify.error(
        t('workOrderActions.deleteFailed'),
        describeApiError(error, tc, {
          codePrefix: 'workorders:workOrderActions.deleteErrorCode',
          fallback: t('workOrderActions.deleteFailed'),
        }),
      );
    }
  };

  // Keep the work order active and reuse the header schedule editor (#2914).
  const handleReasonDialogReschedule = () => {
    const reason = reasonText.trim();
    setReasonDialogOpen(false);
    setReasonText('');
    setScheduleEditOpen(true);
    if (!reason) return;
    addBookingNote.mutate(
      {
        bookingId: workOrder.id,
        category: 'internal',
        content: t('workOrderActions.rescheduleNote', { reason }),
      },
      {
        onError: () => {
          notify.error(tc('error'), t('detail.statusChangeFailed'));
        },
      },
    );
  };

  const handleUndoConfirm = async (reason: string) => {
    try {
      const result = await undoWorkOrder.mutateAsync({
        id: workOrder.id,
        reason,
      });
      setUndoDialogOpen(false);
      // The status the undo restored; `workOrder` is still the pre-undo snapshot (#3439).
      notify.success(t('undoSuccess'), t('undoSuccessDesc', { status: t(`status.${result.currentStatus}`) }));
    } catch (error: unknown) {
      // Was `t('undoFailedDesc', { error: error.message })` against a bare
      // `"{{error}}"` translation, so the whole toast body was the HTTP
      // status line — on the app's most consequential action (#2716).
      notify.error(t('undoFailed'), describeApiError(error, tc, { fallback: t('undoFailedDesc') }));
    }
  };

  const handleStepperStatusChange = (targetStatus: string) => {
    switch (targetStatus) {
      case 'confirmed':
        setConfirmMethodOpen(true);
        break;
      case 'pending':
        setAcceptQuoteDialogOpen(true);
        break;
      case 'vehicle_arrived':
        void handleVehicleArrived();
        break;
      case 'in_progress':
        if (workOrder.status === 'paused') {
          void handleResumeWork();
        } else {
          handleStartWork();
        }
        break;
      case 'paused':
        handlePauseWork();
        break;
      case 'ready_to_invoice':
        handleReadyToInvoice();
        break;
      case 'cancelled':
        openReasonDialog('cancel');
        break;
      case 'quote_rejected':
        void handleRejectQuote();
        break;
    }
  };

  const handleMileageSave = async (mileage: number) => {
    try {
      await updateBooking.mutateAsync({
        id: workOrder.id,
        data: { mileage },
      });
      notify.success(t('detail.mileageUpdated'), t('detail.mileageUpdatedDesc'));
    } catch {
      notify.error(tc('error'), t('detail.mileageUpdateFailed'));
    }
  };

  const contactPersonName = workOrder.customer.customerType === 'business' && workOrder.contactPerson
    ? `${workOrder.contactPerson.firstName} ${workOrder.contactPerson.lastName}`.trim()
    : '';
  const vehicleMakeModel = [workOrder.vehicle.make, workOrder.vehicle.model].filter(Boolean).join(' ');
  const vehicleLocationSummary = workOrder.vehicleLocation
    ? `${t(`vehicleLocation.${workOrder.vehicleLocation}`)}${workOrder.vehicleLocationDetail ? ` • ${workOrder.vehicleLocationDetail}` : ''}`
    : '';

  const cardSections: Record<DetailCardId, { title: string; summary: string; content: React.ReactNode; actions?: React.ReactNode }> = {
    customer_vehicle: {
      title: t('detail.customerAndVehicle'),
      summary: [
        getCustomerDisplayName(workOrder.customer),
        contactPersonName,
        workOrder.vehicle.registrationNumber,
        vehicleMakeModel,
        vehicleLocationSummary,
      ].filter(Boolean).join(' • '),
      content: (
        <WorkOrderTabbedCard
          columns={[
            {
              title: t('customer'),
              ariaLabel: t('customer'),
              tabs: [
                {
                  label: t('detail.infoTab'),
                  content: (
                    <CustomerInfoTab
                      workOrder={workOrder}
                      t={t}
                      ordererPartyPath={ordererPartyPath}
                    />
                  ),
                },
                {
                  label: t('parties.title'),
                  content: (
                    <>
                      <WorkOrderPartiesPanel workOrderId={workOrder.id} />
                      <NoWhatsAppRecipients workOrderId={workOrder.id} />
                    </>
                  ),
                },
              ],
            },
            {
              title: t('vehicle'),
              ariaLabel: t('vehicle'),
              tabs: [
                {
                  label: t('detail.infoTab'),
                  content: (
                    <VehicleInfoTab
                      workOrder={workOrder}
                      mileageSaving={updateBooking.isPending}
                      onMileageSave={handleMileageSave}
                      onOpenVehicleCard={() => setVehicleCardOpen(true)}
                    />
                  ),
                },
                {
                  label: t('vehicleLocation.title'),
                  content: (
                    <VehicleLocationCard
                      workOrderId={workOrder.id}
                      currentLocation={workOrder.vehicleLocation}
                      currentLocationDetail={workOrder.vehicleLocationDetail}
                      cardVariant={cardVariant}
                    />
                  ),
                },
                {
                  label: t('vehicleServiceHistory'),
                  content: (
                    <VehicleServiceHistoryCard
                      vehicleId={workOrder.vehicle.id}
                      currentWorkOrderId={workOrder.id}
                      cardVariant={cardVariant}
                    />
                  ),
                },
              ],
            },
          ]}
        />
      ),
    },
    notes: {
      title: t('notes'),
      summary: workOrder.customerNotes?.trim() ? t('notesHistory.customerTab') : '—',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">
              {t('notes')}
            </Typography>
            <Tooltip title={t('notes')}>
              <IconButton
                size="small"
                onClick={() => { setNotesDialogOpen(true); }}
                aria-label={t('notes')}
              >
                <OpenInFullIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <WorkOrderNotesPreview
            bookingId={workOrder.id}
            customerNotes={workOrder.customerNotes}
          />
        </Box>
      ),
    },
    approval: {
      title: t('signature.cardTitle'),
      summary: '',
      content: <WorkOrderApprovalCard workOrderId={workOrder.id} />,
    },
    services: {
      title: t('services'),
      summary: t('detail.servicesSummary', { services: workOrder.services.length, items: workOrder.items.length }),
      content: (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              {t('services')}
            </Typography>
          </Box>

          <WorkOrderServicesSection
            workOrder={workOrder}
            workOrderId={workOrderId}
            t={t}
            expandedServiceIds={expandedServiceIds}
            setExpandedServiceIds={setExpandedServiceIds}
            accordion={accordion}
            activeDragId={activeDragId}
            dndSensors={dndSensors}
            onDragStart={handleDragStart}
            onReorderServices={handleReorderServices}
            onAddService={handleAddService}
            onRemoveService={handleRemoveService}
            onServiceMechanicChange={handleServiceMechanicChange}
            onServiceAction={handleServiceAction}
            setServiceDirtyStates={setServiceDirtyStates}
            updatePending={updateBooking.isPending}
            formatPrice={formatPrice}
          />

          <WorkOrderTimeEntriesSection
            workOrderId={workOrderId}
            services={workOrder.services.map((service) => ({
              bookingServiceId: service.bookingServiceId,
              name: service.name,
            }))}
          />

          <WorkOrderItemsTable
            items={workOrder.items}
            t={t}
            formatPrice={formatPrice}
          />

          {showOffersCard ? <WorkOrderOffersCard workOrder={workOrder} /> : null}
        </Box>
      ),
    },
    service_book: {
      title: tChecklists('booking.title'),
      summary: `${tChecklists('finalInspection.completed')} • ${workOrder.serviceBookChecked ? tChecklists('serviceBook.checked') : tChecklists('serviceBook.title')}`,
      content: (
        <>
          <WorkPathSection
            workOrderId={workOrder.id}
            serviceIds={workOrder.services.map((s) => s.id)}
            cardVariant={cardVariant}
          />
          <ServiceBookChecklistsCard
            workOrderId={workOrder.id}
            serviceIds={workOrder.services.map((s) => s.id)}
            serviceBookChecked={workOrder.serviceBookChecked}
            serviceBookFilled={workOrder.serviceBookFilled}
            serviceBookType={workOrder.serviceBookType}
            finalInspectionCompleted={workOrder.finalInspectionCompleted}
            finalInspectionCompletedAt={workOrder.finalInspectionCompletedAt}
            cardVariant={cardVariant}
          />
          {/* Last in the section because it prints what the cards above hold
              (#2414). Removed in 91e1b24c "can be added inside the combined
              card later" — this is that card, and the button now fetches only
              on click so it adds no queries to opening a work order.
              When service-book is hidden (#2646) the same button is rendered
              below the status info so the report stays reachable. */}
          <DeliveryReportButton workOrder={workOrder} cardVariant={cardVariant} />
        </>
      ),
    },
    parts_orders: {
      title: t('partsOrders.title'),
      summary: partsOrderSummary,
      content: (
        <>
          {/* Mechanic-facing quick parts panel — always shown first */}
          <MechanicPartsPanel workOrderId={workOrder.id} />
          {/* Full PO card for managers / service advisors */}
          <PartsOrdersCard workOrderId={workOrder.id} cardVariant={cardVariant} />
        </>
      ),
    },
    attachments: {
      title: t('attachments.title'),
      summary: [workOrderNumber, workOrder.vehicle?.registrationNumber].filter(Boolean).join(' • '),
      content: (
        <AttachmentsCard
          entityLinks={[
            { entityType: 'booking', entityId: workOrder.id },
            ...(workOrder.vehicle?.id
              ? [{ entityType: 'vehicle' as const, entityId: workOrder.vehicle.id }]
              : []),
          ]}
          primaryEntity={{ entityType: 'booking', entityId: workOrder.id }}
          // Uploads from here belong to this work order; "also on every work
          // order of the vehicle" is an explicit checkbox in the dialog (#3123).
          uploadEntityLinks={[{ entityType: 'booking', entityId: workOrder.id }]}
          optionalUploadLinks={workOrder.vehicle?.id
            ? [{
                link: { entityType: 'vehicle', entityId: workOrder.vehicle.id },
                labelKey: 'attachments.uploadAlsoVehicle',
                hintKey: 'attachments.uploadAlsoVehicleHint',
              }]
            : []}
          // Files linked to the vehicle but not to this work order are shown here
          // for context (#1043) but managed from the vehicle — keep them view-only
          // so deleting from the work order can't cascade the shared file.
          canModify={(attachment) => attachment.entityLinks.some(
            (link) => link.entityType === 'booking' && link.entityId === workOrder.id,
          )}
          defaultTags={[
            workOrderNumber,
            ...(workOrder.vehicle?.registrationNumber ? [workOrder.vehicle.registrationNumber] : []),
          ].filter(Boolean)}
          cardVariant={cardVariant}
        />
      ),
    },
    courtesy: courtesyWorkOrderCardSection(workOrder, t),
    payments: {
      title: t('detail.payments'),
      // Verollinen summa tai ei summaa lainkaan — nettoa ei näytetä verollisen
      // paikalla (docs/VAT-BASIS.md § 5).
      summary: `${workOrder.totalWithVatCents != null ? formatPrice(workOrder.totalWithVatCents) : '—'}${workOrderInvoice ? ` • ${workOrderInvoice.invoiceNumber}` : ''}`,
      // Laskutuksen aloitus asuu Maksut-kortin otsikossa, ei Työt-kortilla (#2093).
      ...(workOrder.status === 'work_completed'
        ? {
            actions: (
              <Button
                variant="contained"
                size="small"
                startIcon={<ReceiptLongIcon />}
                onClick={handleReadyToInvoice}
                disabled={readyToInvoice.isPending || !!workOrder.isInvoicingHold || !!workOrder.isDisputed}
              >
                {t('serviceActions.readyToInvoice')}
              </Button>
            ),
          }
        : {}),
      content: (
        <WorkOrderPayments
          workOrder={workOrder}
          workOrderInvoice={workOrderInvoice}
          cardVariant={cardVariant}
          t={t}
          formatPrice={formatPrice}
          onOpenServiceLabor={handleOpenServiceLabor}
        />
      ),
    },
    events: {
      title: t('detail.events'),
      summary: t('detail.eventsSummary', { count: history.length }),
      content: (
        <WorkOrderNotesHistory
          history={history}
          cardVariant={cardVariant}
        />
      ),
    },
  };

  return (
    <WorkCompletionGateContext.Provider value={completionGate.requestCompletion}>
    <Box>
      <WorkOrderHeader
        workOrder={workOrder}
        workOrderNumber={workOrderNumber}
        onOpenVehicleCard={() => setVehicleCardOpen(true)}
        history={history}
        viewers={viewers}
        t={t}
        tc={tc}
        onClose={onClose}
        onStatusChange={handleStepperStatusChange}
        onUndo={() => setUndoDialogOpen(true)}
        statusChangeDisabled={
          !!workOrder.isDisputed ||
          updateStatus.isPending ||
          vehicleArrived.isPending ||
          setWaitingForParts.isPending ||
          readyToInvoice.isPending ||
          rejectQuote.isPending ||
          dispute.isPending ||
          resolveDispute.isPending ||
          invoicingHold.isPending ||
          resolveInvoicingHold.isPending ||
          cancelWorkOrder.isPending
        }
        undoDisabled={
          !!workOrder.isDisputed ||
          !!workOrder.isInvoicingHold ||
          undoWorkOrder.isPending
        }
        scheduleSlot={(
          <WorkOrderScheduleBar
            workOrder={workOrder}
            mechanics={mechanics}
            editing={scheduleEditOpen}
            onEditingChange={setScheduleEditOpen}
            onDirtyChange={setScheduleDirty}
            onAcceptQuote={handleAcceptQuote}
            onSendQuote={() => { setSendQuoteDialogOpen(true); }}
          />
        )}
        statusActions={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {hasPendingOffer && (
              <Chip
                size="small"
                color="warning"
                data-testid="offers-waiting-badge"
                label={t('offers.waitingBadge', {
                  count: pendingOfferCount,
                  amount: formatCentsForDisplay(awaitingOfferCents),
                })}
              />
            )}
            <WorkOrderStatusActions
              workOrder={workOrder}
              t={t}
              tc={tc}
              setWaitingForPartsPending={setWaitingForParts.isPending}
              partsReceivedPending={partsReceived.isPending}
              disputePending={dispute.isPending}
              invoicingHoldPending={invoicingHold.isPending}
              cancelWorkOrderPending={cancelWorkOrder.isPending}
              rejectQuotePending={rejectQuote.isPending}
              onAcceptQuote={handleAcceptQuote}
              onSendQuote={() => { setSendQuoteDialogOpen(true); }}
              onRejectQuote={handleRejectQuote}
              onSetWaitingForParts={handleSetWaitingForParts}
              onPartsReceived={handlePartsReceived}
              onRequestApproval={handleRequestApproval}
              onOpenReasonDialog={openReasonDialog}
              onPrint={handlePrintWorkOrder}
              hasPendingOffer={hasPendingOffer}
              onDeleteWorkOrder={hasPermission('work_orders.delete') ? () => { setDeleteDialogOpen(true); } : undefined}
              deleteWorkOrderPending={deleteWorkOrder.isPending}
            />
          </Box>
        )}
      />

      <WorkOrderPrintDialog
        open={printDialogOpen}
        onClose={() => { setPrintDialogOpen(false); }}
        workOrderId={workOrder.id}
      />

      <QuoteHistory key={workOrder.id} workOrder={workOrder} />

      <WorkOrderEmailDeliveries key={workOrder.id} workOrderId={workOrder.id} />

      <WorkOrderStatusInfo
        workOrder={workOrder}
        t={t}
        resolveDisputePending={resolveDispute.isPending}
        resolveInvoicingHoldPending={resolveInvoicingHold.isPending}
        onResolveDispute={handleResolveDispute}
        onResolveInvoicingHold={handleResolveInvoicingHold}
      />

      {workOrder.dropoff ? (
        <DropoffBanner workOrderId={workOrder.id} dropoff={workOrder.dropoff} />
      ) : null}

      {serviceBookAudit.serviceBookOverrideReason ? (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="service-book-override-audit">
          {t('serviceBook.overrideVisible', {
            reason: serviceBookAudit.serviceBookOverrideReason,
            at: serviceBookAudit.serviceBookOverrideAt
              ? new Date(serviceBookAudit.serviceBookOverrideAt).toLocaleString()
              : '—',
          })}
        </Alert>
      ) : null}

      {/* Service-book card owns this button when that module is on. When the
          workshop hides service-book/checklists (#2646) the report still
          prints inspection/checklist/work-path data on demand, so it lives
          here rather than disappearing with the card. */}
      {!serviceBookEnabled && (
        <Box sx={{ mb: 1 }} data-testid="delivery-report-standalone">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('deliveryReport.standaloneHint')}
          </Typography>
          <DeliveryReportButton workOrder={workOrder} cardVariant={cardVariant} />
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, mb: 0.5 }}>
        <Button
          size="small"
          variant="text"
          startIcon={<DragIndicatorIcon fontSize="small" />}
          onClick={toggleHandlesVisible}
        >
          {handlesVisible ? t('detail.hideReorderHandles') : t('detail.showReorderHandles')}
        </Button>
        <Tooltip title={t('detail.accordionModeHint')}>
          <FormControlLabel
            control={<Switch size="small" checked={accordion} onChange={toggleAccordion} />}
            label={<Typography variant="body2">{t('detail.accordionMode')}</Typography>}
            sx={{ mr: 0 }}
          />
        </Tooltip>
      </Box>

      <DndContext
        sensors={sectionDndSensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => reorderCards(event, availableCardIds)}
      >
        <SortableContext items={orderedCardIds} strategy={verticalListSortingStrategy}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {orderedCardIds.map((cardId) => {
              const section = cardSections[cardId];
              return (
                <SortableCollapsibleCard
                  key={cardId}
                  id={cardId}
                  title={section.title}
                  summary={section.summary}
                  expanded={cardId === 'services' && servicesNavExpand ? true : expandedCards[cardId]}
                  onToggle={() => toggleDetailCard(cardId)}
                  variant={cardVariant}
                  sortable={handlesVisible}
                  {...(section.actions != null ? { actions: section.actions } : {})}
                  contentSx={cardId === 'services' ? servicesCardContentSx : embeddedCardContentSx}
                >
                  {section.content}
                </SortableCollapsibleCard>
              );
            })}
          </Box>
        </SortableContext>
      </DndContext>

      <WorkOrderNotesDialog
        open={notesDialogOpen}
        onClose={() => { setNotesDialogOpen(false); }}
        bookingId={workOrder.id}
        customerNotes={workOrder.customerNotes}
      />

      <ConfirmationMethodDialog
        open={confirmMethodOpen}
        onConfirm={(method) => void handleConfirmationMethodSelect(method)}
        onCancel={() => setConfirmMethodOpen(false)}
        loading={confirmBooking.isPending}
      />

      <VehicleLocationDialog
        open={locationDialogOpen}
        workOrderId={workOrder.id}
        onClose={() => { setLocationDialogOpen(false); }}
      />

      <AcceptQuoteScheduleDialog
        open={acceptQuoteDialogOpen}
        workOrder={workOrder}
        onConfirm={handleConfirmAcceptQuote}
        onCancel={() => setAcceptQuoteDialogOpen(false)}
        loading={acceptQuote.isPending}
      />

      <SendQuoteDialog
        open={sendQuoteDialogOpen}
        workOrder={workOrder}
        onConfirm={handleConfirmSendQuote}
        onCancel={() => { setSendQuoteDialogOpen(false); }}
        loading={sendQuote.isPending}
      />

      <MileageRequiredDialog
        open={mileageDialogOpen}
        onConfirm={handleMileageConfirm}
        onCancel={handleMileageCancel}
        loading={mileageDialogLoading}
        latestMileage={workOrder.vehicle.mileage != null ? { mileage: workOrder.vehicle.mileage, recordedAt: workOrder.updatedAt } : null}
      />

      <PauseWorkDialog
        open={pauseDialogOpen}
        workOrderId={workOrder.id}
        hasAssignedLift={!!workOrder.assignedLiftId}
        bookingServiceId={pauseServiceId ?? undefined}
        onClose={() => { setPauseDialogOpen(false); setPauseServiceId(null); }}
      />

      {/* Mounted only while open: its queries then run only when asked for. */}
      {vehicleCardOpen && (
        <WorkOrderVehicleCard
          vehicleId={workOrder.vehicle.id}
          open
          onClose={() => setVehicleCardOpen(false)}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title={t('workOrderActions.deleteConfirmTitle')}
        message={t('workOrderActions.deleteConfirmMessage', { number: workOrderNumber })}
        confirmText={tc('delete')}
        confirmColor="error"
        loading={deleteWorkOrder.isPending}
        onConfirm={() => { void handleDeleteWorkOrder(); }}
        onCancel={() => { setDeleteDialogOpen(false); }}
      />

      {/* Undo confirmation dialog */}
      {history && history.length > 0 && (
        <UndoConfirmDialog
          open={undoDialogOpen}
          onClose={() => setUndoDialogOpen(false)}
          onConfirm={handleUndoConfirm}
          oldStatus={history[0]?.oldStatus || ''}
          newStatus={history[0]?.newStatus || ''}
          isTerminalState={['completed', 'cancelled', 'quote_rejected'].includes(workOrder.status)}
        />
      )}

      <WorkOrderReasonDialog
        open={reasonDialogOpen}
        type={reasonDialogType}
        value={reasonText}
        onChange={setReasonText}
        onClose={() => { setReasonDialogOpen(false); }}
        onSubmit={(event) => { void handleReasonDialogSubmit(event); }}
        submitting={dispute.isPending || invoicingHold.isPending || cancelWorkOrder.isPending || reopenService.isPending}
        {...(reasonDialogType === 'cancel' ? { onReschedule: handleReasonDialogReschedule } : {})}
      />

      <PreInvoiceChecklistDialog
        open={readyChecklistOpen}
        stage="ready_to_invoice"
        onClose={() => { setReadyChecklistOpen(false); }}
        onConfirm={() => { void submitReadyToInvoice(); }}
        submitting={readyToInvoice.isPending}
        workOrderId={workOrder.id}
        finalInspectionCompleted={workOrder.finalInspectionCompleted === true}
        laborCount={workOrder.services.reduce((sum, service) => sum + service.laborItems.length, 0)}
        partsCount={workOrder.services.reduce((sum, service) => sum + service.parts.length, 0)}
        serviceBook={{
          pending: serviceBookEnabled
            && workOrder.serviceBookFilled !== true
            && !serviceBookAudit.serviceBookOverrideReason,
          required: isServiceBookApplicable(workOrder) && !canOverrideServiceBook,
        }}
      />

      {completionGate.dialog}

      {canOverrideServiceBook ? (
        <ServiceBookOverrideDialog
          open={serviceBookOverrideOpen}
          value={serviceBookOverrideReason}
          onChange={setServiceBookOverrideReason}
          onClose={() => { setServiceBookOverrideOpen(false); }}
          onSubmit={() => { void handleServiceBookOverrideSubmit(); }}
          submitting={updateServiceBook.isPending || readyToInvoice.isPending}
        />
      ) : null}

    </Box>
    </WorkCompletionGateContext.Provider>
  );
}
